import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TimelineItem from './TimelineItem'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import BundleAndSaveHotelTile from './BundleAndSaveHotelTile'
import type { BundleOfferPackageOptionView } from 'selectors/offerPage/bundledOfferPageSelectors'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BundleAndSaveOptionPriceDetails from './BundleAndSaveOptionPriceDetails'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import BodyText from 'LuxKit/Typography/BodyText'
import {
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DAY_ONLY,
  INTL_DMY_CASUAL_SHORT_FORMAT,
} from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'

const Container = styled.div`
  position: relative;
  padding: ${rem(20)};

  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${(props) => props.theme.palette.neutral.surface.normalBackground};

  display: grid;
  grid-template:
    'tiles' max-content
    'pricing' min-content;

  grid-row-gap: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(32)};
    grid-template:
      'tiles pricing' min-content
      'tiles .' auto / 1fr ${rem(214)};

    grid-column-gap: ${rem(40)};
  }

  &.bundle-and-save-v2 {
    grid-template:
      'pricing' min-content
      'tiles' max-content;

    ${mediaQueryUp.desktop} {
      grid-template-areas: 'pricing tiles';
      grid-template-columns: ${rem(214)};
    }
  }

  & > .tiles {
    grid-area: tiles;
  }

  & > .pricing {
    grid-area: pricing;
  }
`

const DateLabel = styled.span`
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.XS};
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  margin: ${rem(2)};
`

const OuterTileGroup = styled.div`
  & .nested-child-spacer {
    display: none;
  }

  &:not(:last-child) {
    & .nested-child-spacer {
      display: block;
    }
  }

  & .nested-dates-spacer {
    display: none;
  }

  &.has-dates {
    & .nested-dates-spacer {
      display: block;
    }
  }
`

const InnerTileGroup = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: min-content 1fr;
  grid-gap: ${rem(12)};
`

interface Props {
  bundledOffersState: OfferPageState['bundledOffers']
  bundledPackages: Array<App.HotelPackage>
  packageView: BundleOfferPackageOptionView
  fetching?: boolean
  availableForPurchase: boolean
}

function BundleOptionTileGroup({
  bundledOffersState,
  bundledPackages,
  packageView,
  fetching,
  availableForPurchase,
}: Props) {
  const intl = useIntl()
  const soldOutLabel = intl.formatMessage({
    id: 'nvp9aIirkpk84iDxiuIp',
    defaultMessage: 'SOLD OUT',
    description: 'Urgency label shown when a bundle and save package is sold out',
  })
  const getPkgByOfferId = useCallback(
    (offerId: string) => {
      return bundledPackages.find((pkg) => pkg.offerId === offerId)
    },
    [bundledPackages],
  )
  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])
  const hotelAvailable = packageView.hotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable && !fetching

  return (
    <Container className={clsx({
      'bundle-and-save-v2': isBundleAndSaveV2Enabled(),
    })}
    >
      <Group direction="vertical" className="tiles" gap={16}>
        {!isBundleAndSaveV2Enabled() && <CSSBreakpoint max="tablet">
          <VerticalSpacer gap={12}>
            {(!availableForPurchase || !canPurchase) && !fetching && (
              <div>
                <UrgencyLabel variant="critical2">{soldOutLabel}</UrgencyLabel>
              </div>
            )}
            <Heading variant="heading4">{packageView.bundlePackageName}</Heading>
          </VerticalSpacer>
        </CSSBreakpoint>}
        <Group direction="vertical">
          {bundledOffers.map((offerState) => {
            let checkInOutDate: string | null = null
            const checkInDate = offerState.checkInDate

            if (checkInDate) {
              const checkOutDate = moment(checkInDate).clone().add(offerState.duration, 'days')
              const isTheSameDate = moment(checkInDate).isSame(checkOutDate, 'month')
              const formattedCheckIn = isTheSameDate
                ? intlDateFormatter(checkInDate, INTL_DAY_ONLY, intl.locale)
                : intlDateFormatter(checkInDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)
              const formattedCheckOut = intlDateFormatter(checkOutDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale)
              checkInOutDate = formattedCheckIn + ' - ' + formattedCheckOut
            }

            const pkg = getPkgByOfferId(offerState.offer?.id ?? '')

            if (!pkg) {
              return null
            }

            return (
              <OuterTileGroup
                key={offerState.offer?.id ?? ''}
                className={clsx({ 'has-dates': !!checkInOutDate })}
              >
                {checkInOutDate && (
                  <Group direction="horizontal" verticalAlign="center" gap={12}>
                    <DateLabel>
                      <BodyText
                        variant="large"
                        weight="bold"
                        colour="dark-contrast"
                      >
                        {checkInOutDate}
                      </BodyText>
                    </DateLabel>
                  </Group>
                )}
                <InnerTileGroup>
                  <TimelineItem icon={checkInOutDate ? null : LineBedIcon} />
                  <Group direction="vertical" gap={20}>
                    <div role="presentation" className="nested-dates-spacer" />
                    <BundleAndSaveHotelTile
                      pkg={pkg}
                      offer={offerState.offer!}
                      availableForPurchase={availableForPurchase}
                    />
                    <div role="presentation" className="nested-child-spacer" />
                  </Group>
                </InnerTileGroup>
              </OuterTileGroup>
            )
          })}
        </Group>
      </Group>
      <Group className="pricing" direction="vertical" gap={16}>
        <CSSBreakpoint min={isBundleAndSaveV2Enabled() ? undefined : 'desktop'}>
          <VerticalSpacer gap={12}>
            {availableForPurchase && !canPurchase && !fetching && !hotelAvailable && (
              <div>
                <UrgencyLabel variant="critical2">{soldOutLabel}</UrgencyLabel>
              </div>
            )}
            <Heading variant="heading4">{packageView.bundlePackageName}</Heading>
            {isBundleAndSaveV2Enabled() && <BodyText variant="medium">{packageView.bundlePackageDescription}</BodyText>}
          </VerticalSpacer>
        </CSSBreakpoint>
        <BundleAndSaveOptionPriceDetails
          packageView={packageView}
          fetching={fetching}
          availableForPurchase={availableForPurchase}
        />
      </Group>
    </Container>
  )
}

export default withOfferPageState((state) => {
  return {
    bundledOffersState: state.bundledOffers,
  }
}, BundleOptionTileGroup)
